import React, { useCallback, useEffect, useState } from 'react';
import { Layout, Row, Col, Typography, Checkbox, Avatar, Tag, Button, Input, Table, Flex, Card, Popconfirm, Form, DatePicker, Spin, Space, Drawer, message } from 'antd';
import { SearchOutlined, ExportOutlined, EyeOutlined, DeleteOutlined, EditOutlined, LoadingOutlined, UserAddOutlined } from '@ant-design/icons';
import { PiUserDuotone, PiUserFocusDuotone, PiUserPlusDuotone, PiUsersDuotone, PiUserSoundDuotone } from 'react-icons/pi';
import dayjs from 'dayjs';
const { Title } = Typography;

const PaidCustomers = () => {
    const [dataLoading, setDataLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modifyPlanDrawer, setModifyPlanDrawer] = useState(false);
    const [planform] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const currentMonth = dayjs().format('YYYY-MM');
    const previousMonth = dayjs().subtract(1, 'month').format('YYYY-MM');

    const fetchCustomerOverviewData = useCallback(async () => {
        const url = `${window.API_BASE_URL}/superadmin/paid-users`;
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            console.log(data);
            if (response.ok) {
                setUsers(data?.users);
                setFilteredData(data?.users);
            }
        } catch (error) {
            console.error('Error during fetching store data:', error);
        } finally {
            setDataLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            await fetchCustomerOverviewData();
            setDataLoading(false);
        };
        fetchData();
    }, [fetchCustomerOverviewData]);


    const currentMonthActiveUsers = users?.filter(user =>
        user.status === 'active' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === currentMonth
    )?.length || 0;
    const previousMonthActiveUsers = users?.filter(user =>
        user.status === 'active' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === previousMonth
    )?.length || 0;
    const activechange = previousMonthActiveUsers
        ? ((currentMonthActiveUsers - previousMonthActiveUsers) / previousMonthActiveUsers) * 100
        : 0;
    const changeActiveSign = activechange >= 0 ? '+' : '';
    const changeActiveClass = activechange >= 0 ? 'positive' : 'negative';


    const currentMonthInActiveUsers = users?.filter(user =>
        user.status === 'inactive' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === currentMonth
    )?.length || 0;
    const previousMonthInActiveUsers = users?.filter(user =>
        user.status === 'inactive' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === previousMonth
    )?.length || 0;
    const changeinactive = previousMonthInActiveUsers
        ? ((currentMonthInActiveUsers - previousMonthInActiveUsers) / previousMonthInActiveUsers) * 100
        : 0;
    const changeInActiveSign = changeinactive >= 0 ? '+' : '';
    const changeInActiveClass = changeinactive >= 0 ? 'positive' : 'negative';

    const columns = [
        {
            title: <Checkbox />,
            dataIndex: 'checkbox',
            render: () => <Checkbox />,
        },
        {
            title: 'Avatar',
            dataIndex: 'avatar',
            render: (_, record) => <Avatar><PiUserDuotone size={18} /></Avatar>,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            render: (_, record) => (
                <div>
                    <Title level={5} style={{ margin: 0 }}>{record?.user_name}</Title>
                    <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 400, color: '#8f8f8f' }}>{record?.user_email}</Title>
                </div>
            ),
        },
        {
            title: 'Phone #.',
            dataIndex: 'user_phone',
            render: (_, record) => (`${record?.user_country_code}${record?.user_phone}`),
        },
        {
            title: 'Created at',
            dataIndex: 'created_at',
        },
        {
            title: 'Plan start date',
            dataIndex: 'start_date',
            render: (timestamp) => timestamp !== "" ? dayjs.unix(timestamp).format('YYYY-MM-DD') : '',
        },
        {
            title: 'Plan end date',
            dataIndex: 'end_date',
            render: (timestamp) => timestamp !== "" ? dayjs.unix(timestamp).format('YYYY-MM-DD') : '',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button icon={<EditOutlined />} onClick={() => handleEditPlan(record)} />
                    <Button icon={<EyeOutlined />} style={{ color: 'green', borderColor: 'green' }} />
                    <Popconfirm title="Delete user" description="Are you sure to delete this user?" okText="Yes" cancelText="No" onConfirm={() => handleDelete(record)}>
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </div>
            ),
        },
    ];
    
    const handleEditPlan = async (record) => {
        setSelectedRecord(record);
        setModifyPlanDrawer(true);
    };

    const [planLoading, setPlanLoading] = useState(false);

    const handleNewPlanSubmit = async () => {
        setPlanLoading(true);
        const token = localStorage.getItem("adminLoginToken");
        try {
            const values = await planform.validateFields();
            const startDateTimestamp = values.start_date ? dayjs(values.start_date).unix() : null;
            const endDateTimestamp = values.end_date ? dayjs(values.end_date).unix() : null;
            const response = await fetch(`${window.API_BASE_URL}/users/plan-modify`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ user_id: selectedRecord.user_id, payment_amount: values.payment_amount, transaction_id: values.transaction_id, start_date: startDateTimestamp, end_date: endDateTimestamp }),
            });

            if (response.ok) {
                const result = await response.json();
                message.success('Plan updated successfully.');
            } else {
                message.error("Error updating plan!");
            }
        } catch (error) {
            console.error("Error saving content:", error);
        } finally {
            setPlanLoading(false);
            setModifyPlanDrawer(false);
        }
    };

    const handleDelete = async () => {

    };

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchText(value);
        const filtered = users.filter((data) => {
            return (
                data.name.toLowerCase().includes(value) ||
                data.email.toLowerCase().includes(value) ||
                data.role.toLowerCase().includes(value) ||
                data.company.toLowerCase().includes(value) ||
                data.paymentType.toLowerCase().includes(value) ||
                data.status.toLowerCase().includes(value)
            );
        });
        setFilteredData(filtered);
    };
    
    const handleExport = () => {
        // Implement export functionality here
        console.log('Export data to CSV or Excel');
    };
    
    return (
        <Layout className='contentLayout'>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
                <Title level={3} style={{ display: 'flex', alignItems: 'center', margin: 0 }}><b>Paid Customers</b></Title>
            </div>
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightgreen'>
                        <Flex gap={16} justify='space-between'>
                            <div>
                                <Title className='cardtitleOne' level={5}>Active Users</Title>
                                <Title className='cardtitleTwo' level={3}>{users?.filter(user => user.status === 'active')?.length} <span className={changeActiveClass}>({changeActiveSign}{activechange.toFixed(0)}%)</span></Title>
                                <Title className='cardtitleThree' level={5}>Total Users</Title>
                            </div>
                            <div>
                                <div className='cardIcon'>
                                    <PiUsersDuotone size={36} /> 
                                </div>
                            </div>
                        </Flex>
                    </Card>
                </Col> 
                <Col span={12}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightorange'>
                        <Flex gap={16} justify='space-between'>
                            <div>
                                <Title className='cardtitleOne' level={5}>Inactive Users</Title>
                                <Title className='cardtitleTwo' level={3}>{users?.filter(user => user.status === 'inactive')?.length} <span className={changeInActiveClass}>({changeInActiveSign}{changeinactive.toFixed(0)}%)</span></Title>
                                <Title className='cardtitleThree' level={5}>Last Week Analytics</Title>
                            </div>
                            <div>
                                <div className='cardIcon'>
                                    <PiUserFocusDuotone size={36} />
                                </div>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={24}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                        <Input
                            placeholder="Search..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={handleSearch}
                            style={{ width: 200 }}
                        />
                        <Button
                            icon={<ExportOutlined />}
                            onClick={handleExport}
                        >
                            Export
                        </Button>
                    </div>
                    <Table
                        dataSource={searchText ? filteredData : users}
                        columns={columns}
                        pagination={{ pageSize: 10 }}
                        loading={dataLoading}
                    />
                </Col>
            </Row>
            <Drawer
                title={<><UserAddOutlined /> Modify Plan</>}
                width={720}
                onClose={() => setModifyPlanDrawer(false)}
                open={modifyPlanDrawer}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setModifyPlanDrawer(false)}>Cancel</Button>
                            {loading ? (
                                <Button type="primary">
                                    <Spin style={{ color: "#fff" }} indicator={<LoadingOutlined spin />} />
                                </Button>
                            ) : (
                                <Button onClick={handleNewPlanSubmit} type="primary" loading={planLoading}>
                                    <span>Submit</span>
                                </Button>
                            )}
                        </Space>
                    </div>
                }
            >
                <Form layout="vertical" form={planform}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="payment_amount"
                                label="Payment Amount"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter payment amount',
                                    },
                                ]}
                            >
                                <Input placeholder="Please enter payment amount" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="transaction_id"
                                label="Transaction Id"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter transaction id',
                                    },
                                ]}
                            >
                                <Input placeholder="Please enter transaction id" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="start_date"
                                label="Plan start date"
                                rules={[
                                    {
                                        required: false,
                                        message: 'Please enter plan start date',
                                    },
                                ]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="end_date"
                                label="Plan end date"
                                rules={[
                                    {
                                        required: false,
                                        message: 'Please enter Plan end date',
                                    },
                                ]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Drawer>
        </Layout>
    );
};
export default PaidCustomers;